'use client';

import { useTranslations } from 'next-intl';
import type { ReactNode, Ref } from 'react';

import type { TriggerPriceType } from '@flipster/apis/v2/trade/types';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import { Explain } from '@flipster/react/design-utils/explain';
import Formatted from '@flipster/react/design-utils/formatted';
import { css, cx, type Styles } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { NumberInputWithFrame } from '../../../input/number-input-with-frame';
import type { ModalBasisMode } from '../../calculator/tpsl-basis';
import { TriggerPriceTypeSelect } from '../../trigger-price-type-select';
import { BasisSelect } from '../tpsl-basis-field/basis-select';
import { modalSecondaryPlaceholder } from '../tpsl-basis-field/placeholders';
import { getExpectedModalSecondarySign } from '../tpsl-basis-field/signed-secondary';
import { modalBasisModes, type TpslBasisMode } from '../tpsl-basis-field/types';

const defaultQuickPercents = [-1, -0.1, 0.1, 1] as const;

export interface ModalPriceBasisRowProps {
  // Absolute price (left)
  priceValue: string;
  onPriceChange: (value: string) => void;
  pricePrecision: number;
  pricePlaceholder?: string;
  priceAutoFocus?: boolean;
  /** Optional ref on the absolute price input (e.g. Advanced modal Tab order). */
  priceInputRef?: Ref<HTMLInputElement>;
  triggerPriceType: TriggerPriceType;
  onTriggerPriceTypeChange: (value: TriggerPriceType) => void;
  showTriggerPriceTypeSelect?: boolean;
  // Secondary basis (right)
  basisMode: ModalBasisMode;
  onBasisModeChange: (mode: ModalBasisMode) => void;
  /** When false, show a static Change(%) label (no dropdown). Default true. */
  showBasisSelect?: boolean;
  secondaryValue: string;
  onSecondaryChange: (value: string) => void;
  /** Secondary 포커스 중 live entry recompute를 막기 위한 콜백 */
  onSecondaryFocus?: () => void;
  onSecondaryBlur?: () => void;
  secondaryPrecision?: number;
  quoteCurrency: string;
  /** Required for expected-sign force / placeholder wiring. */
  leg: 'tp' | 'sl';
  side: 'Long' | 'Short';
  // Quick ±% (Change mode only)
  showQuickPercents?: boolean;
  quickPercents?: ReadonlyArray<number>;
  onQuickPercent?: (percent: number) => void;
  /** Trigger-row inline error (below ±% when Change, else below dual-field). */
  errorMessage?: ReactNode;
  /** Position first-time coachmark on basis select (optional). */
  firstTimeTooltip?: ReactNode;
  firstTimeTooltipOpen?: boolean;
  onFirstTimeTooltipOpenChange?: (open: boolean) => void;
  // BBO: hide dual-field, show Best Counterparty
  isBbo?: boolean;
  bboLabel?: string;
  disabled?: boolean;
  css?: Styles;
}

/**
 * Variant B dual-field: absolute price + basis secondary (+ optional ±% for Change).
 * BBO ON → Best Counterparty placeholder only.
 */
export const ModalPriceBasisRow = ({
  priceValue,
  onPriceChange,
  pricePrecision,
  pricePlaceholder,
  priceAutoFocus,
  priceInputRef,
  triggerPriceType,
  onTriggerPriceTypeChange,
  showTriggerPriceTypeSelect = true,
  basisMode,
  onBasisModeChange,
  showBasisSelect = true,
  secondaryValue,
  onSecondaryChange,
  onSecondaryFocus,
  onSecondaryBlur,
  secondaryPrecision = 2,
  quoteCurrency,
  leg,
  side,
  showQuickPercents,
  quickPercents = defaultQuickPercents,
  onQuickPercent,
  errorMessage,
  firstTimeTooltip,
  firstTimeTooltipOpen,
  onFirstTimeTooltipOpenChange,
  isBbo = false,
  bboLabel = 'Best Counterparty',
  disabled,
  css: cssProps,
}: ModalPriceBasisRowProps) => {
  const t = useTranslations();
  const showPercents = showQuickPercents ?? basisMode === 'change';
  const expectedSign = getExpectedModalSecondarySign({ mode: basisMode, leg, side });

  const basisSelect = showBasisSelect ? (
    <BasisSelect
      mode={basisMode}
      modes={modalBasisModes as ReadonlyArray<TpslBasisMode>}
      onModeChange={(mode) => onBasisModeChange(mode as ModalBasisMode)}
      quoteCurrency={quoteCurrency}
      disabled={disabled}
    />
  ) : (
    <span className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-4', whiteSpace: 'nowrap' })}>
      {`${t('Autogen_common.r251126.s_Change')}(%)`}
    </span>
  );

  const basisSelectWithOptionalTooltip =
    showBasisSelect && firstTimeTooltip != null && firstTimeTooltipOpen != null ? (
      <Tooltip.Root open={firstTimeTooltipOpen} onOpenChange={onFirstTimeTooltipOpenChange} placement='top'>
        <Tooltip.Trigger asChild>
          <span className={css({ display: 'inline-flex' })}>{basisSelect}</span>
        </Tooltip.Trigger>
        <Tooltip.Content portal zIndex={token('zIndex.modal')} bottomSheetTitle={firstTimeTooltip}>
          {firstTimeTooltip}
        </Tooltip.Content>
      </Tooltip.Root>
    ) : (
      basisSelect
    );

  if (isBbo) {
    return (
      <div className={cx(css(cssProps), stack({ gap: 12 }))}>
        <div
          className={css({
            h: 48,
            px: 12,
            rounded: 8,
            border: '1px solid',
            borderColor: 'border.default.level-1',
            bg: 'background.default.level-1',
            display: 'flex',
            alignItems: 'center',
            textStyle: 'Body/XL/Monospace/Regular',
            color: 'content.default.level-3',
          })}
        >
          {bboLabel}
        </div>
        {errorMessage ? <Explain.Error>{errorMessage}</Explain.Error> : null}
      </div>
    );
  }

  return (
    <div className={cx(css(cssProps), stack({ gap: 12 }))}>
      <div className={hstack({ gap: 8, alignItems: 'stretch' })}>
        <NumberInputWithFrame
          ref={priceInputRef}
          precision={pricePrecision}
          value={priceValue}
          onChange={onPriceChange}
          placeholder={pricePlaceholder}
          autoFocus={priceAutoFocus}
          disabled={disabled}
          className={css({ flex: 1, minW: 0 })}
          suffix={
            showTriggerPriceTypeSelect ? (
              <TriggerPriceTypeSelect
                value={triggerPriceType}
                onChange={onTriggerPriceTypeChange}
                placement='in-input'
                disabled={disabled}
              />
            ) : undefined
          }
        />
        <NumberInputWithFrame
          precision={secondaryPrecision}
          value={secondaryValue}
          onChange={onSecondaryChange}
          onFocus={onSecondaryFocus}
          onBlur={onSecondaryBlur}
          placeholder={modalSecondaryPlaceholder}
          withPlus={false}
          expectedSign={expectedSign}
          disabled={disabled}
          className={css({ flex: 1, minW: 0 })}
          suffix={basisSelectWithOptionalTooltip}
        />
      </div>
      {showPercents && onQuickPercent && (
        <div className={hstack({ gap: 4 })}>
          {quickPercents.map((percent) => (
            <button
              key={percent}
              type='button'
              disabled={disabled}
              onClick={() => onQuickPercent(percent)}
              className={css({
                flex: '1 0',
                px: 10,
                py: 6,
                rounded: 8,
                textAlign: 'center',
                textStyle: 'Text/M/Monospace/Regular',
                color: 'content.default.level-5',
                bg: 'background.default.level-3',
                cursor: disabled ? 'not-allowed' : 'pointer',
              })}
            >
              <Formatted.Percent value={percent} withPlus />
            </button>
          ))}
        </div>
      )}
      {errorMessage ? <Explain.Error>{errorMessage}</Explain.Error> : null}
    </div>
  );
};
